import React, { useState, useEffect, useContext } from 'react';
import EntriesFilterRow from './EntriesFilterRow';
import DateSelector from 'components/Screening/filters/DateSelector';
import { Modal } from '@mui/material';
import ModalLayout from '../../../DataFiltering/ModalLayout';
import { format } from 'date-fns';
import DatahubAPI from 'api/DatahubAPI';
import CasesAPI from 'api/CasesAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import { getNewDocs } from 'utils/resultsComparison';
import AutomationAPI from 'api/AutomationAPI';
import pako from 'pako';
import { Buffer } from 'buffer';
import { useSnackbar } from 'notistack';
import PaymentsAPI from 'api/PaymentsAPI';

interface value {
  searchVal: string,
  type: string,
}
export interface automationParams {
  values?: value[];
  entState?: IEntityState;
  comState?: ICommodityState;
  row?: string;
}

interface IEntityState {
  fuzzy: boolean,
  dateRange: string[],
  lenses: string[],
  countries: string[],
  agencies: string[],
  lists: string[],
}

interface ICommodityState {
  fuzzy: boolean,
  dateRange: string[],
  comLenses: string[],
  countries: string[],
  agencies: string[],
  lists: string[],
}


interface IFiltersProps {
  activeFilter: string,
  options: any[];
  selectedOpts: string[];
  setSelectedOpts: any;
  handleFilterChange: any;
  cur_case: any;
  screenParmsModal: boolean;
  setScreenParamsModal: any;
  screeningCus: any;
}

const EntriesFilters: React.FC<IFiltersProps> = ({ activeFilter, options, selectedOpts, setSelectedOpts, handleFilterChange, cur_case, screenParmsModal, setScreenParamsModal, screeningCus }) => {
  const [ openFilter, setOpenFilter ] = useState(false);
  const [ openDate, setOpenDate ] = useState(false);
  const [ appliedFilter, setAppliedFilter ] = useState<any>({});
  const [ loading, setLoading ] = useState(false);
  const [ submit, setSubmit ] = useState<any>({
    trigger: false,
    reason: '',
    location: ''
  });

  const reloadPage = () => {
    document.body.style.opacity = '0.5'; // Fade effect
    setTimeout(() => window.location.reload(), 3000);
  };

  const { enqueueSnackbar } = useSnackbar();
  const { cur_user } = useContext(GlobalContext);
  const dt = new Date();
  const dtWithoutTz = new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000);
  const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');

  const [ entityState, setEntityState ] = useState({
    lenses: cur_case.data.entityFilter.lenses,
    countries: cur_case.data.entityFilter.countries,
    agencies: cur_case.data.entityFilter.agencies,
    lists: cur_case.data.entityFilter.lists,
    fuzzy: cur_case.data.entityFilter.entRes,
    dateRange: cur_case.data.entityFilter.filterDate
  });

  const [ commodityState, setCommodityState ] = useState({
    comLenses: cur_case.data.commodityFilter.lenses,
    countries: cur_case.data.commodityFilter.countries,
    agencies: cur_case.data.commodityFilter.agencies,
    lists: cur_case.data.commodityFilter.lists,
    fuzzy: cur_case.data.commodityFilter.entRes,
    dateRange: cur_case.data.commodityFilter.filterDate
  });

  const handleDateChange = (newValue: any) => {
    const arr: any = [ ...newValue ];
    if (newValue !== null) {
      arr.forEach((dt: any, index: number) => {
        if (dt !== null) {
          const dtWithoutTz = new Date(dt.valueOf() + dt.getTimezoneOffset() * 60 * 1000);
          const formatted_dt = format(dtWithoutTz, 'yyyy-MM-dd');
          arr[index] = formatted_dt;
        }
      });
    }
    setEntityState({ ...entityState, dateRange: arr });
    setCommodityState({ ...commodityState, dateRange: arr });
  };

  const handleClear = () => {
    setEntityState({
      lenses: cur_case.data.entityFilter.lenses,
      countries: cur_case.data.entityFilter.countries,
      agencies: cur_case.data.entityFilter.agencies,
      lists: cur_case.data.entityFilter.lists,
      fuzzy: cur_case.data.entityFilter.entRes,
      dateRange: cur_case.data.entityFilter.filterDate
    });
    setCommodityState({
      comLenses: cur_case.data.commodityFilter.lenses,
      countries: cur_case.data.commodityFilter.countries,
      agencies: cur_case.data.commodityFilter.agencies,
      lists: cur_case.data.commodityFilter.lists,
      fuzzy: cur_case.data.commodityFilter.entRes,
      dateRange: cur_case.data.commodityFilter.filterDate
    });
    setSubmit({ reason: 'apply', trigger: !submit.trigger, location: 'Entity' });
  };

  const compressData = (data: any) => {
    const stringifiedData = JSON.stringify(data);
    const compressed = pako.gzip(stringifiedData);
    return Buffer.from(compressed).toString('base64'); // Convert to base64 for storage or transmission
  };

  const decompressData = (compressedData: any) => {
    const compressedBuffer = Buffer.from(compressedData[0], 'base64');
    const decompressed = pako.ungzip(compressedBuffer, { to: 'string' });
    return JSON.parse(decompressed);
  };

  const triggerMeter = async(): Promise<any> => {
    const res = await PaymentsAPI.updateMeter(
      {
        type: 'screening_forms_meter'
      },
      screeningCus.data.id
    );
    if (res?.success) {
      return res?.result;
    }
  };

  const autoAPICall = async(rowsArr: any) => {
    let allResults: any = [];
    let resCount = 0;
    let totalResults = 0;

    const findMatchingIndex = (val: string, arr: any[]): number | false => {
      for (let i = 0; i < arr.length; i++) {
        if (arr[i].field === val) {
          return i;
        }
      }
      return false;
    };

    for (const [ i, row ] of rowsArr.entries()) {
      const query: automationParams = {
        values: row,
        entState: entityState,
        comState: commodityState,
        row: i + 1,
      };

      const res = await AutomationAPI.automationDataSearch(query);
      resCount++;

      if (res?.success) {
        if (res?.result[0].result?.body?.hits?.total?.value) {
          totalResults = totalResults + res?.result[0].result?.body?.hits?.total?.value;
        }

        if (res?.result[1].result?.body?.hits?.total?.value) {
          totalResults = totalResults + res?.result[1].result?.body?.hits?.total?.value;
        }

        const entityResArr: any[] = [];
        const commodityResArr: any[] = [];

        await res?.result[0].result?.body?.hits?.hits?.forEach((hit: any) => {
          const val = hit.matched_queries ? hit.matched_queries[0] : '';
          const i = findMatchingIndex(val, entityResArr);
          if (i !== false) {
            entityResArr[i].hits++;
            entityResArr[i].docs.push(hit._source);
          } else {
            entityResArr.push({ field: val, row: res.result[0].row, dataSet: 'Entites', hits: 1, docs: [ hit._source ] });
          }
        });
        await res?.result[1].result?.body?.hits?.hits?.forEach((hit: any) => {
          const val = hit.matched_queries ? hit.matched_queries[0] : '';
          const i = findMatchingIndex(val, commodityResArr);
          if (i !== false) {
            commodityResArr[i].hits++;
            commodityResArr[i].docs.push(hit._source);
          } else {
            commodityResArr.push({ field: val, row: res.result[1].row, dataSet: 'Commodities', hits: 1, docs: [ hit._source ] });
          }
        });

        allResults = [ ...allResults, ...entityResArr, ...commodityResArr ];

        if (resCount === rowsArr.length) {
          triggerMeter();
          return {
            all: [ ...allResults ],
            tot: totalResults
          };
        };
      } else {
        const error_msg = 'Error screening data. An API call has failed or timed out.';
        enqueueSnackbar(error_msg, { variant: 'error' });
        return {
          all: [ ],
          tot: 0
        };
      }
    };
  };

  const handleTicket = async(values: any) => {
    const update = {
      _id: cur_case.data._id,
      ...values.basics,
      ...values,
    };
    const resp = await CasesAPI.updateCase(update);

    if (resp?.success) {
      enqueueSnackbar('Successfully updated case', { variant: 'success' });
      setLoading(false);
      setScreenParamsModal(false);
      reloadPage();
    } else if (resp) {
      const error_msg = 'Error updating case. Uploaded file(s) may be too large.';
      enqueueSnackbar(error_msg, { variant: 'error' });
      setLoading(false);
      setScreenParamsModal(false);
    }
  };

  const screenTicket = async() => {
    const curDocInfo = cur_case?.data?.results[cur_case.data.results.length - 1]?.docInfo;
    setLoading(true);

    const entFilterQuery = {
      lenses: entityState.lenses,
      countries: entityState.countries,
      agencies: entityState.agencies,
      lists: entityState.lists,
      entRes: entityState.fuzzy,
      filterDate: entityState.dateRange
    };

    const comFilterQuery = {
      lenses: commodityState.comLenses,
      countries: commodityState.countries,
      agencies: commodityState.agencies,
      lists: commodityState.lists,
      entRes: commodityState.fuzzy,
      filterDate: commodityState.dateRange
    };

    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: 'updated the screening parameters of the ticket and ran a new screen',
      timeStamp: Date.now()
    };

    const query = {
      entityFilter: entFilterQuery,
      commodityFilter: comFilterQuery,
      lastUpdate: formatted_dt,
      status: 'Complete',
      activity: [ ...cur_case.data.activity, actObj ],
    };


    const obj = await autoAPICall(curDocInfo);
    if (obj) {
      const newArr = cur_case.data.results.length > 0 ? getNewDocs(decompressData(cur_case.data.results[cur_case.data.results.length - 1].allResults), obj.all) : [];

      const ticketQuery = {
        ...query,
        curTotal: obj.tot,
        curNew: newArr.length,
        results: [ ...cur_case.data.results, {
          allResults: compressData(obj.all),
          allTotal: obj.tot,
          newResults: compressData(newArr),
          newTotal: newArr.length,
          date: formatted_dt,
          docInfo: curDocInfo
        } ]
      };
      handleTicket(ticketQuery);
    } else {
      handleTicket(query);
    }
  };

  const saveParams = async() => {
    const entFilterQuery = {
      lenses: entityState.lenses,
      countries: entityState.countries,
      agencies: entityState.agencies,
      lists: entityState.lists,
      entRes: entityState.fuzzy,
      filterDate: entityState.dateRange
    };

    const comFilterQuery = {
      lenses: commodityState.comLenses,
      countries: commodityState.countries,
      agencies: commodityState.agencies,
      lists: commodityState.lists,
      entRes: commodityState.fuzzy,
      filterDate: commodityState.dateRange
    };

    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: 'updated the screening parameters of the ticket',
      timeStamp: Date.now()
    };
    const res = await CasesAPI.updateCase({
      _id: cur_case.data._id,
      entityFilter: entFilterQuery,
      commodityFilter: comFilterQuery,
      activity: [ ...cur_case.data.activity, actObj ],
    });
    if (res?.success) {
      enqueueSnackbar('Screening Parameters Updated!', { variant: 'success' });
      setScreenParamsModal(false);
    } else {
      enqueueSnackbar('There was an error updating this ticket', { variant: 'error' });
      setScreenParamsModal(false);
    }
  };

  return (
    <>
      <div className='entries-frame'>
        <div className='entries-frame-head'>
          <div className='entries-frame-head-cont'>
            <div className='entries-frame-head-cont' style={{ gap: 'var(--spacing-md, 8px)' }}>
              <div className='entries-frame-head-cont-text'>Filter Results</div>

              <div className='entries-frame-footer-cont'>
                <div className='entries-frame-footer-text' onClick={() => setScreenParamsModal(true)}>Screening Parameters</div>
              </div>

            </div>
          </div>
        </div>
        <div className='entries-frame-body'>
          <div className='entries-frame-body-cont'>
            <div className='entries-frame-body-cont-lenses'>
              <div className='entries-frame-body-cont-lenses-head' onClick={() => {
                if (activeFilter === 'BITE Lens') {
                  setOpenFilter(!openFilter);
                } else {
                  setOpenFilter(true);
                  handleFilterChange('BITE Lens');
                }
              }}>
                <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
                  <g clipPath='url(#clip0_26068_74293)'>
                    <path d='M16.0616 11.5058C13.9443 13.0121 11.1671 13.1794 8.90777 12M1.93833 6.49417C4.06079 4.98425 6.84633 4.81978 9.10862 6.00862M11.5303 10.5933C13.6953 9.22876 14.9489 6.7306 14.7007 4.13417M6.43638 7.42778C4.29174 8.79682 3.05219 11.2827 3.29908 13.8658M11.6452 7.60278C11.5454 5.04638 10.0087 2.71263 7.63658 1.62917M6.35671 10.4442C6.47334 12.9822 8.00571 15.294 10.3633 16.3708M14.3033 3.6967C17.2322 6.62563 17.2322 11.3744 14.3033 14.3033C11.3744 17.2322 6.62562 17.2322 3.6967 14.3033C0.767767 11.3744 0.767767 6.62562 3.6967 3.6967C6.62563 0.767767 11.3744 0.767767 14.3033 3.6967ZM11.1213 6.87868C12.2929 8.05025 12.2929 9.94975 11.1213 11.1213C9.94975 12.2929 8.05025 12.2929 6.87868 11.1213C5.70711 9.94975 5.70711 8.05025 6.87868 6.87868C8.05025 5.70711 9.94975 5.70711 11.1213 6.87868Z' stroke={activeFilter === 'BITE Lens' ? '#4895EF' : '#BDBEC1'} strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                  </g>
                  <defs>
                    <clipPath id='clip0_26068_74293'>
                      <rect width='18' height='18' fill='white' />
                    </clipPath>
                  </defs>
                </svg>
                <div className={activeFilter === 'BITE Lens' ? 'entries-frame-body-cont-lenses-head-text-active' : 'entries-frame-body-cont-lenses-head-text'}>
                Lenses
                </div>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' cursor={'pointer'} style={{ transform: activeFilter === 'BITE Lens' && openFilter ? 'none' : 'rotate(90deg)' }}>
                  <path d='M17.0002 9.1697C16.8128 8.98345 16.5594 8.87891 16.2952 8.87891C16.031 8.87891 15.7776 8.98345 15.5902 9.1697L12.0002 12.7097L8.46019 9.1697C8.27283 8.98345 8.01938 8.87891 7.75519 8.87891C7.49101 8.87891 7.23756 8.98345 7.05019 9.1697C6.95646 9.26266 6.88207 9.37326 6.8313 9.49512C6.78053 9.61698 6.75439 9.74769 6.75439 9.8797C6.75439 10.0117 6.78053 10.1424 6.8313 10.2643C6.88207 10.3861 6.95646 10.4967 7.05019 10.5897L11.2902 14.8297C11.3832 14.9234 11.4938 14.9978 11.6156 15.0486C11.7375 15.0994 11.8682 15.1255 12.0002 15.1255C12.1322 15.1255 12.2629 15.0994 12.3848 15.0486C12.5066 14.9978 12.6172 14.9234 12.7102 14.8297L17.0002 10.5897C17.0939 10.4967 17.1683 10.3861 17.2191 10.2643C17.2699 10.1424 17.296 10.0117 17.296 9.8797C17.296 9.74769 17.2699 9.61698 17.2191 9.49512C17.1683 9.37326 17.0939 9.26266 17.0002 9.1697Z' fill={activeFilter === 'BITE Lens' ? '#4895EF' : '#BDBEC1'} />
                </svg>
              </div>
              {options && openFilter && activeFilter === 'BITE Lens' && options.map((row: any, index: number) => (
                <EntriesFilterRow
                  key={index}
                  name={row}
                  selectedItems={selectedOpts}
                  setSelectedItems={setSelectedOpts}
                />
              ))}
            </div>
            <div className='entries-frame-body-cont-divider'>
              <svg xmlns='http://www.w3.org/2000/svg' width='283' height='2' viewBox='0 0 283 2' fill='none'>
                <path d='M0 1H283' stroke='#2E3036' />
              </svg>
            </div>
            <div className='entries-frame-body-cont-lenses' >
              <div className='entries-frame-body-cont-lenses-head' onClick={() => {
                if (activeFilter === 'Source Country') {
                  setOpenFilter(!openFilter);
                } else {
                  setOpenFilter(true);
                  handleFilterChange('Source Country');
                }
              }}>
                <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
                  <g clipPath='url(#clip0_26068_74311)'>
                    <path d='M2.30699 5.61212L5.52302 7.90924C5.69036 8.02877 5.77404 8.08854 5.86161 8.10231C5.93866 8.11442 6.01758 8.10221 6.08736 8.06737C6.16668 8.02778 6.22837 7.94552 6.35176 7.781L7.03134 6.87489C7.06623 6.82836 7.08367 6.8051 7.10418 6.78503C7.12239 6.76722 7.14236 6.7513 7.1638 6.73753C7.18794 6.72203 7.21451 6.71022 7.26765 6.6866L10.1691 5.39707C10.2894 5.34361 10.3495 5.31688 10.3949 5.27514C10.435 5.23822 10.4665 5.193 10.4874 5.14263C10.511 5.08568 10.5153 5.02002 10.5241 4.88869L10.7365 1.70205M10.125 10.125L12.087 10.9659C12.3146 11.0634 12.4285 11.1122 12.4888 11.1932C12.5417 11.2643 12.5677 11.3518 12.5622 11.4403C12.556 11.5411 12.4873 11.6441 12.3499 11.8502L11.4281 13.2328C11.363 13.3305 11.3304 13.3794 11.2874 13.4147C11.2493 13.446 11.2054 13.4695 11.1583 13.4838C11.105 13.5 11.0463 13.5 10.9289 13.5H9.43246C9.27695 13.5 9.19919 13.5 9.13303 13.474C9.07461 13.451 9.02289 13.4137 8.98262 13.3656C8.93701 13.311 8.91242 13.2373 8.86325 13.0897L8.32859 11.4858C8.29959 11.3988 8.28508 11.3553 8.28127 11.311C8.27788 11.2718 8.28069 11.2323 8.28958 11.194C8.29962 11.1508 8.32013 11.1097 8.36115 11.0277L8.76728 10.2154C8.8498 10.0504 8.89106 9.96789 8.95461 9.91666C9.01061 9.87151 9.07842 9.84342 9.14994 9.83575C9.23111 9.82704 9.31863 9.85621 9.49367 9.91456L10.125 10.125ZM16.5 9C16.5 13.1421 13.1421 16.5 9 16.5C4.85786 16.5 1.5 13.1421 1.5 9C1.5 4.85786 4.85786 1.5 9 1.5C13.1421 1.5 16.5 4.85786 16.5 9Z' stroke={activeFilter === 'Source Country' ? '#4895EF' : '#BDBEC1'} strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                  </g>
                  <defs>
                    <clipPath id='clip0_26068_74311'>
                      <rect width='18' height='18' fill='white' />
                    </clipPath>
                  </defs>
                </svg>
                <div className={activeFilter === 'Source Country' ? 'entries-frame-body-cont-lenses-head-text-active' : 'entries-frame-body-cont-lenses-head-text'}>
                Listing Country
                </div>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' cursor={'pointer'} style={{ transform: activeFilter === 'Source Country' && openFilter ? 'none' : 'rotate(90deg)' }}>
                  <path d='M17.0002 9.1697C16.8128 8.98345 16.5594 8.87891 16.2952 8.87891C16.031 8.87891 15.7776 8.98345 15.5902 9.1697L12.0002 12.7097L8.46019 9.1697C8.27283 8.98345 8.01938 8.87891 7.75519 8.87891C7.49101 8.87891 7.23756 8.98345 7.05019 9.1697C6.95646 9.26266 6.88207 9.37326 6.8313 9.49512C6.78053 9.61698 6.75439 9.74769 6.75439 9.8797C6.75439 10.0117 6.78053 10.1424 6.8313 10.2643C6.88207 10.3861 6.95646 10.4967 7.05019 10.5897L11.2902 14.8297C11.3832 14.9234 11.4938 14.9978 11.6156 15.0486C11.7375 15.0994 11.8682 15.1255 12.0002 15.1255C12.1322 15.1255 12.2629 15.0994 12.3848 15.0486C12.5066 14.9978 12.6172 14.9234 12.7102 14.8297L17.0002 10.5897C17.0939 10.4967 17.1683 10.3861 17.2191 10.2643C17.2699 10.1424 17.296 10.0117 17.296 9.8797C17.296 9.74769 17.2699 9.61698 17.2191 9.49512C17.1683 9.37326 17.0939 9.26266 17.0002 9.1697Z' fill={activeFilter === 'Source Country' ? '#4895EF' : '#BDBEC1'} />
                </svg>
              </div>
              {options && openFilter && activeFilter === 'Source Country' && options.map((row: any, index: number) => (
                <EntriesFilterRow
                  key={index}
                  name={row}
                  selectedItems={selectedOpts}
                  setSelectedItems={setSelectedOpts}
                />
              ))}
            </div>
            <div className='entries-frame-body-cont-divider'>
              <svg xmlns='http://www.w3.org/2000/svg' width='283' height='2' viewBox='0 0 283 2' fill='none'>
                <path d='M0 1H283' stroke='#2E3036' />
              </svg>
            </div>
            <div className='entries-frame-body-cont-lenses' >
              <div className='entries-frame-body-cont-lenses-head' onClick={() => {
                if (activeFilter === 'Agency') {
                  setOpenFilter(!openFilter);
                } else {
                  setOpenFilter(true);
                  handleFilterChange('Agency');
                }
              }}>
                <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'>
                  <path d='M3.75 6.75026V12.7503M7.125 6.75026V12.7503M10.875 6.75026V12.7503M14.25 6.75026V12.7503M2.25 13.9503L2.25 14.5503C2.25 14.9703 2.25 15.1803 2.33175 15.3408C2.40365 15.4819 2.51839 15.5966 2.65951 15.6685C2.81994 15.7503 3.02996 15.7503 3.45 15.7503H14.55C14.97 15.7503 15.1801 15.7503 15.3405 15.6685C15.4816 15.5966 15.5963 15.4819 15.6683 15.3408C15.75 15.1803 15.75 14.9703 15.75 14.5503V13.9503C15.75 13.5302 15.75 13.3202 15.6683 13.1598C15.5963 13.0186 15.4816 12.9039 15.3405 12.832C15.1801 12.7503 14.97 12.7503 14.55 12.7503H3.45C3.02996 12.7503 2.81994 12.7503 2.65951 12.832C2.51839 12.9039 2.40365 13.0186 2.33175 13.1598C2.25 13.3202 2.25 13.5302 2.25 13.9503ZM8.73968 2.30811L3.18968 3.54144C2.85439 3.61595 2.68674 3.65321 2.5616 3.74336C2.45122 3.82288 2.36455 3.93093 2.31086 4.05593C2.25 4.19765 2.25 4.36939 2.25 4.71286L2.25 5.55026C2.25 5.9703 2.25 6.18032 2.33175 6.34075C2.40365 6.48187 2.51839 6.59661 2.65951 6.66851C2.81994 6.75026 3.02996 6.75026 3.45 6.75026H14.55C14.97 6.75026 15.1801 6.75026 15.3405 6.66851C15.4816 6.59661 15.5963 6.48187 15.6683 6.34075C15.75 6.18032 15.75 5.9703 15.75 5.55026V4.71287C15.75 4.36939 15.75 4.19765 15.6891 4.05593C15.6355 3.93093 15.5488 3.82288 15.4384 3.74336C15.3133 3.65321 15.1456 3.61595 14.8103 3.54144L9.26032 2.30811C9.16317 2.28652 9.1146 2.27573 9.06552 2.27142C9.02192 2.2676 8.97808 2.2676 8.93448 2.27142C8.8854 2.27573 8.83683 2.28652 8.73968 2.30811Z' stroke={activeFilter === 'Agency' ? '#4895EF' : '#BDBEC1'} strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round' />
                </svg>
                <div className={activeFilter === 'Agency' ? 'entries-frame-body-cont-lenses-head-text-active' : 'entries-frame-body-cont-lenses-head-text'}>
                Agency
                </div>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' cursor={'pointer'} style={{ transform: activeFilter === 'Agency' && openFilter ? 'none' : 'rotate(90deg)' }}>
                  <path d='M17.0002 9.1697C16.8128 8.98345 16.5594 8.87891 16.2952 8.87891C16.031 8.87891 15.7776 8.98345 15.5902 9.1697L12.0002 12.7097L8.46019 9.1697C8.27283 8.98345 8.01938 8.87891 7.75519 8.87891C7.49101 8.87891 7.23756 8.98345 7.05019 9.1697C6.95646 9.26266 6.88207 9.37326 6.8313 9.49512C6.78053 9.61698 6.75439 9.74769 6.75439 9.8797C6.75439 10.0117 6.78053 10.1424 6.8313 10.2643C6.88207 10.3861 6.95646 10.4967 7.05019 10.5897L11.2902 14.8297C11.3832 14.9234 11.4938 14.9978 11.6156 15.0486C11.7375 15.0994 11.8682 15.1255 12.0002 15.1255C12.1322 15.1255 12.2629 15.0994 12.3848 15.0486C12.5066 14.9978 12.6172 14.9234 12.7102 14.8297L17.0002 10.5897C17.0939 10.4967 17.1683 10.3861 17.2191 10.2643C17.2699 10.1424 17.296 10.0117 17.296 9.8797C17.296 9.74769 17.2699 9.61698 17.2191 9.49512C17.1683 9.37326 17.0939 9.26266 17.0002 9.1697Z' fill={activeFilter === 'Agency' ? '#4895EF' : '#BDBEC1'} />
                </svg>
              </div>
              {options && openFilter && activeFilter === 'Agency' && options.map((row: any, index: number) => (
                <EntriesFilterRow
                  key={index}
                  name={row}
                  selectedItems={selectedOpts}
                  setSelectedItems={setSelectedOpts}
                />
              ))}
            </div>
            <div className='entries-frame-body-cont-divider'>
              <svg xmlns='http://www.w3.org/2000/svg' width='283' height='2' viewBox='0 0 283 2' fill='none'>
                <path d='M0 1H283' stroke='#2E3036' />
              </svg>
            </div>
            <div className='entries-frame-body-cont-lenses' >
              <div className='entries-frame-body-cont-lenses-head' onClick={() => {
                if (activeFilter === 'List Name') {
                  setOpenFilter(!openFilter);
                } else {
                  setOpenFilter(true);
                  handleFilterChange('List Name');
                }
              }}>
                <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none'><path d='M15.75 2.25H2.25M15.75 15.75H2.25M3.75 9C3.75 8.30109 3.75 7.95163 3.86418 7.67598C4.01642 7.30843 4.30843 7.01642 4.67598 6.86418C4.95163 6.75 5.30109 6.75 6 6.75L12 6.75C12.6989 6.75 13.0484 6.75 13.324 6.86418C13.6916 7.01642 13.9836 7.30843 14.1358 7.67598C14.25 7.95163 14.25 8.30109 14.25 9C14.25 9.69891 14.25 10.0484 14.1358 10.324C13.9836 10.6916 13.6916 10.9836 13.324 11.1358C13.0484 11.25 12.6989 11.25 12 11.25L6 11.25C5.30109 11.25 4.95163 11.25 4.67597 11.1358C4.30843 10.9836 4.01642 10.6916 3.86418 10.324C3.75 10.0484 3.75 9.69891 3.75 9Z' stroke={activeFilter === 'List Name' ? '#4895EF' : '#BDBEC1'} strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'></path></svg>
                <div className={activeFilter === 'List Name' ? 'entries-frame-body-cont-lenses-head-text-active' : 'entries-frame-body-cont-lenses-head-text'}>
                List
                </div>
                <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' cursor={'pointer'} style={{ transform: activeFilter === 'List Name' && openFilter ? 'none' : 'rotate(90deg)' }}>
                  <path d='M17.0002 9.1697C16.8128 8.98345 16.5594 8.87891 16.2952 8.87891C16.031 8.87891 15.7776 8.98345 15.5902 9.1697L12.0002 12.7097L8.46019 9.1697C8.27283 8.98345 8.01938 8.87891 7.75519 8.87891C7.49101 8.87891 7.23756 8.98345 7.05019 9.1697C6.95646 9.26266 6.88207 9.37326 6.8313 9.49512C6.78053 9.61698 6.75439 9.74769 6.75439 9.8797C6.75439 10.0117 6.78053 10.1424 6.8313 10.2643C6.88207 10.3861 6.95646 10.4967 7.05019 10.5897L11.2902 14.8297C11.3832 14.9234 11.4938 14.9978 11.6156 15.0486C11.7375 15.0994 11.8682 15.1255 12.0002 15.1255C12.1322 15.1255 12.2629 15.0994 12.3848 15.0486C12.5066 14.9978 12.6172 14.9234 12.7102 14.8297L17.0002 10.5897C17.0939 10.4967 17.1683 10.3861 17.2191 10.2643C17.2699 10.1424 17.296 10.0117 17.296 9.8797C17.296 9.74769 17.2699 9.61698 17.2191 9.49512C17.1683 9.37326 17.0939 9.26266 17.0002 9.1697Z' fill={activeFilter === 'List Name' ? '#4895EF' : '#BDBEC1'} />
                </svg>
              </div>
              {options && openFilter && activeFilter === 'List Name' && options.map((row: any, index: number) => (
                <EntriesFilterRow
                  key={index}
                  name={row}
                  selectedItems={selectedOpts}
                  setSelectedItems={setSelectedOpts}
                />
              ))}
            </div>
          </div>
        </div>
        <div className='entries-frame-footer'>

        </div>
      </div>
      <Modal
        open={screenParmsModal}
        onClose={() => setScreenParamsModal(false)}
        backdrop='static'
        keyboard={false}
      >
        <ModalLayout
          setFilterMode={setScreenParamsModal}
          submit={submit}
          setSubmit={setSubmit}
          entityState={entityState}
          setEntityState={setEntityState}
          commodityState={commodityState}
          setCommodityState={setCommodityState}
          appliedFilter={appliedFilter}
          handleClear={handleClear}
          setOpenDate={setOpenDate}
          result={cur_case.data.results.length > 0}
          handleSaveChanges={saveParams}
          handleSaveAndResubmit={screenTicket}
          screenLoading={loading}
        />
      </Modal>
      <Modal
        open={openDate}
        onClose={() => setOpenDate(false)}
      >
        <DateSelector
          openDate={openDate}
          setOpenDate={setOpenDate}
          state={entityState}
          handleDateChange={handleDateChange}
          setSubmit={setSubmit}
          submit={submit}
        />
      </Modal>
    </>
  );
};

export default EntriesFilters;
